/**
 * UIScaleManager - Responsive UI scaling for different screen sizes
 *
 * Provides scaling factors for fonts, UI elements, and spacing
 * to ensure readability across devices from phones to desktops.
 */

import { MobileDeviceManager } from './MobileDetection';

/**
 * Reference screen dimensions (desktop 1080p)
 */
const REFERENCE_WIDTH = 1920;
const REFERENCE_HEIGHT = 1080;

/**
 * Minimum scale factors to ensure readability
 */
const MIN_FONT_SCALE = 0.6;
const MIN_UI_SCALE = 0.5;

/**
 * Maximum scale factors to prevent overly large UI
 */
const MAX_FONT_SCALE = 1.5;
const MAX_UI_SCALE = 1.5;

/**
 * Mobile-specific boost factors
 */
const MOBILE_FONT_BOOST = 1.2; // 20% larger fonts on mobile
const MOBILE_UI_BOOST = 1.1;   // 10% larger UI on mobile

/**
 * UI scale information
 */
export interface UIScaleInfo {
  /** Scale factor for fonts (relative to reference) */
  fontScale: number;
  /** Scale factor for UI elements */
  uiScale: number;
  /** Scale factor for spacing/padding */
  spacingScale: number;
  /** Whether this is a mobile device */
  isMobile: boolean;
  /** Current screen width */
  screenWidth: number;
  /** Current screen height */
  screenHeight: number;
  /** Device pixel ratio */
  pixelRatio: number;
}

/**
 * Calculate UI scale based on screen dimensions
 */
function calculateScale(width: number, height: number, isMobile: boolean): UIScaleInfo {
  const pixelRatio = window.devicePixelRatio || 1;

  // Calculate base scale from screen dimensions
  const widthRatio = width / REFERENCE_WIDTH;
  const heightRatio = height / REFERENCE_HEIGHT;

  // Use the smaller ratio to ensure UI fits on screen
  let baseScale = Math.min(widthRatio, heightRatio);

  // Apply mobile boost
  if (isMobile) {
    baseScale *= MOBILE_UI_BOOST;
  }

  // Calculate separate scales for different purposes
  let fontScale = baseScale;
  let uiScale = baseScale;
  let spacingScale = baseScale;

  // Apply mobile font boost
  if (isMobile) {
    fontScale *= MOBILE_FONT_BOOST;
  }

  // Clamp scales to reasonable ranges
  fontScale = Math.max(MIN_FONT_SCALE, Math.min(MAX_FONT_SCALE, fontScale));
  uiScale = Math.max(MIN_UI_SCALE, Math.min(MAX_UI_SCALE, uiScale));
  spacingScale = Math.max(MIN_UI_SCALE, Math.min(MAX_UI_SCALE, spacingScale));

  // For very small screens (phones), use minimum readable sizes
  const minDimension = Math.min(width, height);
  if (minDimension < 400) {
    fontScale = Math.max(fontScale, 0.8);
    uiScale = Math.max(uiScale, 0.7);
  }

  return {
    fontScale,
    uiScale,
    spacingScale,
    isMobile,
    screenWidth: width,
    screenHeight: height,
    pixelRatio,
  };
}

/**
 * UIScaleManager - Singleton for managing UI scaling
 */
export class UIScaleManager {
  private static _instance: UIScaleManager | null = null;

  private _scaleInfo: UIScaleInfo;
  private _listeners: Set<(scale: UIScaleInfo) => void> = new Set();
  private _resizeHandler: (() => void) | null = null;

  private constructor() {
    const deviceManager = MobileDeviceManager.getInstance();
    const isMobile = deviceManager.isMobile;

    this._scaleInfo = calculateScale(
      window.innerWidth,
      window.innerHeight,
      isMobile
    );

    this._setupResizeListener();
  }

  static getInstance(): UIScaleManager {
    if (!UIScaleManager._instance) {
      UIScaleManager._instance = new UIScaleManager();
    }
    return UIScaleManager._instance;
  }

  private _setupResizeListener(): void {
    this._resizeHandler = () => {
      const deviceManager = MobileDeviceManager.getInstance();
      this._scaleInfo = calculateScale(
        window.innerWidth,
        window.innerHeight,
        deviceManager.isMobile
      );
      this._notify();
    };

    window.addEventListener('resize', this._resizeHandler);
  }

  private _notify(): void {
    this._listeners.forEach(listener => listener(this._scaleInfo));
  }

  /**
   * Get current scale info
   */
  get scale(): UIScaleInfo {
    return { ...this._scaleInfo };
  }

  /**
   * Get font scale
   */
  get fontScale(): number {
    return this._scaleInfo.fontScale;
  }

  /**
   * Get UI scale
   */
  get uiScale(): number {
    return this._scaleInfo.uiScale;
  }

  /**
   * Get spacing scale
   */
  get spacingScale(): number {
    return this._scaleInfo.spacingScale;
  }

  /**
   * Check if mobile
   */
  get isMobile(): boolean {
    return this._scaleInfo.isMobile;
  }

  /**
   * Scale a font size
   */
  scaleFontSize(baseSize: number): number {
    return Math.round(baseSize * this._scaleInfo.fontScale);
  }

  /**
   * Scale a UI dimension
   */
  scaleUI(baseSize: number): number {
    return Math.round(baseSize * this._scaleInfo.uiScale);
  }

  /**
   * Scale spacing/padding
   */
  scaleSpacing(baseSize: number): number {
    return Math.round(baseSize * this._scaleInfo.spacingScale);
  }

  /**
   * Get scaled font sizes for common UI elements
   */
  getFontSizes(): {
    tiny: number;
    small: number;
    medium: number;
    large: number;
    xlarge: number;
    title: number;
  } {
    return {
      tiny: this.scaleFontSize(10),
      small: this.scaleFontSize(12),
      medium: this.scaleFontSize(14),
      large: this.scaleFontSize(18),
      xlarge: this.scaleFontSize(24),
      title: this.scaleFontSize(32),
    };
  }

  /**
   * Get scaled spacing values
   */
  getSpacing(): {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
  } {
    return {
      xs: this.scaleSpacing(4),
      sm: this.scaleSpacing(8),
      md: this.scaleSpacing(12),
      lg: this.scaleSpacing(16),
      xl: this.scaleSpacing(24),
    };
  }

  /**
   * Update scales (called on resize)
   */
  update(width: number, height: number): void {
    const deviceManager = MobileDeviceManager.getInstance();
    this._scaleInfo = calculateScale(width, height, deviceManager.isMobile);
    this._notify();
  }

  /**
   * Add scale change listener
   */
  addListener(callback: (scale: UIScaleInfo) => void): void {
    this._listeners.add(callback);
  }

  /**
   * Remove scale change listener
   */
  removeListener(callback: (scale: UIScaleInfo) => void): void {
    this._listeners.delete(callback);
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    this._listeners.clear();
    UIScaleManager._instance = null;
  }
}

/**
 * Helper function to scale a font size
 */
export function scaledFontSize(baseSize: number): number {
  return UIScaleManager.getInstance().scaleFontSize(baseSize);
}

/**
 * Helper function to scale a UI dimension
 */
export function scaledUI(baseSize: number): number {
  return UIScaleManager.getInstance().scaleUI(baseSize);
}

/**
 * Helper function to scale spacing
 */
export function scaledSpacing(baseSize: number): number {
  return UIScaleManager.getInstance().scaleSpacing(baseSize);
}
